import { useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="full-page-loader">
    <Loader2 className="h-8 w-8 animate-spin text-blue-700" aria-label="Loading" />
  </div>
);

export const LoadingRows = ({ rows = 4, testId = "loading-state" }) => (
  <div className="space-y-3" data-testid={testId} aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={`skeleton-row-${i}`} className="h-14 w-full rounded-lg" />
    ))}
  </div>
);

export const ErrorState = ({ message, onRetry, testId = "error-state" }) => (
  <div role="alert" data-testid={testId} className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-rose-800">
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="flex-1 space-y-3">
        <p className="text-sm font-medium">{message}</p>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
            Try again
          </Button>
        )}
      </div>
    </div>
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
    {Icon && <Icon className="mx-auto mb-3 h-10 w-10 text-slate-400" />}
    <h3 className="font-display text-lg font-semibold text-slate-800">{title}</h3>
    {description && <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const usePageTitle = (name) => {
  useEffect(() => {
    document.title = name ? `Warrant Whizz - ${name}` : "Warrant Whizz";
  }, [name]);
};

export const PageHeader = ({ eyebrow, title, description, actions }) => {
  usePageTitle(title);
  return (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="font-data text-xs font-medium uppercase tracking-[0.18em] text-blue-700">{eyebrow}</p>}
      <h1 className="font-display mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
  );
};

export const FieldError = ({ message, testId }) =>
  message ? (
    <p className="mt-1 text-xs font-medium text-rose-600" role="alert" data-testid={testId}>
      {message}
    </p>
  ) : null;
